import {
  ActionReducerMapBuilder,
  Draft,
  PayloadAction,
  Slice,
  SliceCaseReducers,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import type { AppDispatch } from 'store';

import { Authority, ModelError, User, UserState } from 'models';

import { AuthorityRolesEnum, UserType } from 'enums';

import type { RootState } from './rootReducer';
import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from './storeHelpers';

import BebyggelseApi from 'api/bebyggelseApi';
import sha256 from 'crypto-js/sha256';

const SAKOMRADE_BEBYGGELSEINFORMATION = 'Bebyggelseinformation';

const getUser = createAsyncThunk<User, undefined, { rejectValue: ModelError }>(
  'user/fetch',
  async (_undefined: undefined, { rejectWithValue }) => {
    const result = await BebyggelseApi.fetchUser();
    return errorOr<User>(result, rejectWithValue);
  }
);

export const initializeUser = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(getUser());

    const {
      user: { data: user },
      aktivitetDomaner: { data: aktivitetDomaner }
    } = getState();

    let validUser: User | null = null;

    if (user && user.type === UserType.ANONYMOUS) {
      dispatch(userIsAnonymous(true));
    } else {
      if (user && user.type === UserType.EXTERN) {
        const isValidExternAuthority = (authority: Authority): boolean =>
          authority.sakomrade === SAKOMRADE_BEBYGGELSEINFORMATION &&
          Boolean(authority.organisation_namn) &&
          Boolean(authority.organisation_nummer) &&
          Boolean(authority.authority) &&
          user.authorities.some(
            (otherAuthority: Authority) =>
              otherAuthority.role === AuthorityRolesEnum.BEBYGGELSEREGISTRERARE &&
              otherAuthority.organisation_nummer === authority.organisation_nummer
          );

        validUser = {
          ...user,
          authorities: user.authorities.filter(isValidExternAuthority)
        };
      } else if (user && user.type === UserType.INTERN) {
        const validInternalRoles: AuthorityRolesEnum[] = [
          AuthorityRolesEnum.GRANSKARE,
          AuthorityRolesEnum.SBM_HANDLAGGARE
        ];
        const isValidInternAuthourity = (authority: Authority): boolean => validInternalRoles.includes(authority.role);

        const beslutsorganisationTypSBM = aktivitetDomaner?.beslutsorganisationer_sbm.at(0);

        if (beslutsorganisationTypSBM === undefined || aktivitetDomaner?.beslutsorganisationer_sbm.length !== 1) {
          dispatch(userHasNoAuthorization(true));
          console.error('Kan inte fastställa beslutsorganisation för SBM');
          return;
        }

        const appendInternalOrgToInternalRoles = (authority: Authority): Authority => {
          switch (authority.role) {
            case AuthorityRolesEnum.SBM_HANDLAGGARE:
            case AuthorityRolesEnum.GRANSKARE:
              return {
                ...authority,
                organisation_namn: beslutsorganisationTypSBM.organisationsnamn,
                organisation_nummer: beslutsorganisationTypSBM.organisationsnummer
              };
            default:
              return {
                ...authority,
                organisation_namn: 'Okänd',
                organisation_nummer: ''
              };
          }
        };

        validUser = {
          ...user,
          authorities: user.authorities.filter(isValidInternAuthourity).map(appendInternalOrgToInternalRoles)
        };
      }

      if (!validUser) {
        dispatch(userHasNoAuthorization(true));
        return;
      }

      const hash = sha256(validUser.username).toString();
      const storedOrganisationNummer = localStorage.getItem(hash);

      const selectedAuthority: Authority | undefined = validUser.authorities.find(
        (authority: Authority) => authority.organisation_nummer === storedOrganisationNummer
      );

      if (selectedAuthority) {
        dispatch(setActiveOrganisationAndRoles({ selectedOrganisation: selectedAuthority, user: validUser }));
      } else if (validUser.authorities.length > 0) {
        dispatch(setActiveOrganisationAndRoles({ selectedOrganisation: validUser.authorities[0], user: validUser }));
      } else {
        dispatch(userHasNoAuthorization(true));
      }
    }
  };
};

const initialState: UserState = {
  requestStatus: 'idle',
  data: undefined,
  requestType: undefined,
  currentRequestId: undefined,
  error: undefined,
  selected_organisation: undefined,
  hasNoAuthorization: false,
  isAnonymous: false,
  rolesInSelectedOrganisation: []
};

const userSlice: Slice<UserState, SliceCaseReducers<UserState>, 'user'> = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setActiveOrganisationAndRoles: (
      state: Draft<UserState>,
      payloadAction: PayloadAction<{ selectedOrganisation: Authority; user: User }>
    ): void => {
      if (state.data) {
        state.selected_organisation = payloadAction.payload.selectedOrganisation;

        state.rolesInSelectedOrganisation = payloadAction.payload.user.authorities
          .filter(
            (authority) =>
              authority.organisation_nummer === payloadAction.payload.selectedOrganisation.organisation_nummer
          )
          .map((authority) => authority.role);

        state.data = payloadAction.payload.user;
      }
    },
    userIsAnonymous: (state: Draft<UserState>, payloadAction: PayloadAction<boolean>): void => {
      state.isAnonymous = payloadAction.payload;
    },
    userHasNoAuthorization: (state: Draft<UserState>, payloadAction: PayloadAction<boolean>): void => {
      state.hasNoAuthorization = payloadAction.payload;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<UserState>>) => {
    builder.addCase(getUser.pending, (state: Draft<UserState>, action: PendingActionType) => pending(state, action));
    builder.addCase(getUser.fulfilled, (state: Draft<UserState>, action: FulfilledActionType<User>) =>
      fulfilled(state, action)
    );
    builder.addCase(getUser.rejected, (state: Draft<UserState>, action: RejectedActionType): void =>
      rejected(state, action)
    );
  }
});

export const { setActiveOrganisationAndRoles, userHasNoAuthorization, userIsAnonymous } = userSlice.actions;

export default userSlice.reducer;
